import { useState, useRef, useCallback } from 'react';
import { aiApi } from '../api';
import { AIMessage, ConversationThread, ClickableSuggestion } from '../components/types';

// Utility functions
export const toReadableErrorMessage = (value: any, fallback: string): string => {
  if (typeof value === 'string' && value.trim().length > 0) return value;
  if (value && typeof value === 'object') {
    if (typeof value.message === 'string' && value.message.trim().length > 0) {
      return value.message;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
};

export const extractAssistantText = (content: string): string => {
  const trimmed = String(content || '').trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return trimmed;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed.assistant_message === 'string' && parsed.assistant_message.trim().length > 0) {
      return parsed.assistant_message;
    }
    return trimmed;
  } catch {
    return trimmed;
  }
};

const CONVERSATIONS_PAGE_SIZE = 20;

interface UseConversationsParams {
  userId: number | undefined;
  companyId: number | null | undefined;
  generateWelcomeMessage: () => string;
  generateSuggestions: () => ClickableSuggestion[];
  // Shared state from provider
  messages: AIMessage[];
  setMessages: React.Dispatch<React.SetStateAction<AIMessage[]>>;
  welcomeMessage: string;
  setWelcomeMessage: React.Dispatch<React.SetStateAction<string>>;
  suggestions: ClickableSuggestion[];
  setSuggestions: React.Dispatch<React.SetStateAction<ClickableSuggestion[]>>;
}

interface UseConversationsReturn {
  conversationId: string | null;
  setConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  conversations: ConversationThread[];
  setConversations: React.Dispatch<React.SetStateAction<ConversationThread[]>>;
  isConversationsLoading: boolean;
  hasMoreConversations: boolean;
  loadConversationHistory: () => Promise<void>;
  loadMoreConversations: () => Promise<void>;
  handleSelectConversation: (conversation: ConversationThread) => Promise<void>;
  handleNewChat: () => Promise<void>;
  handleDeleteConversation: (convId: string, e: React.MouseEvent) => Promise<void>;
  handleExportConversation: (conv: ConversationThread, e: React.MouseEvent) => void;
  ensureConversationId: () => Promise<string | null>;
  generateSmartTitle: (userMessage: string, aiResponse: string) => string;
  hasHydratedConversation: React.MutableRefObject<boolean>;
}

export function useConversations({
  userId,
  companyId,
  generateWelcomeMessage,
  generateSuggestions,
  messages,
  setMessages,
  setWelcomeMessage,
  setSuggestions,
}: UseConversationsParams): UseConversationsReturn {
  const [conversationId, setConversationId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('ai_active_conversation') || null;
    } catch {
      return null;
    }
  });
  const [conversations, setConversations] = useState<ConversationThread[]>([]);
  const [isConversationsLoading, setIsConversationsLoading] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [conversationsOffset, setConversationsOffset] = useState(0);

  const hasHydratedConversation = useRef(false);

  const loadConversationHistory = useCallback(async () => {
    try {
      setIsConversationsLoading(true);
      const conversationList = await aiApi.listConversations(CONVERSATIONS_PAGE_SIZE, 0);
      const threads = (conversationList.conversations || []) as ConversationThread[];
      setConversations(threads);
      setConversationsOffset(threads.length);
      setHasMoreConversations(threads.length >= CONVERSATIONS_PAGE_SIZE);

      let activeConversationId = conversationId || threads?.[0]?.id;

      if (activeConversationId) {
        const activeConversation = threads.find(t => t.id === activeConversationId);

        if (activeConversation) {
          setConversationId(activeConversationId);

          try {
            const history = await aiApi.getConversationMessages(activeConversationId, 100, 0);
            if (history.messages && history.messages.length > 0) {
              const validMessages = history.messages.map((msg: any) => ({
                id: msg.id,
                role: msg.role,
                content: extractAssistantText(toReadableErrorMessage(msg?.content, '')),
                timestamp: new Date(msg.created_at),
                meta: msg.metadata
              }));
              setMessages(validMessages);
            } else {
              setMessages([]);
            }
          } catch {
            setMessages([]);
          }

          setWelcomeMessage(activeConversation.welcome_message || '');
        } else if (threads.length > 0) {
          const fallback = threads[0]!;
          setConversationId(fallback.id);
          localStorage.setItem('ai_active_conversation', fallback.id);
          try {
            const history = await aiApi.getConversationMessages(fallback.id, 100, 0);
            if (history.messages && history.messages.length > 0) {
              setMessages(history.messages.map((msg: any) => ({
                id: msg.id, role: msg.role,
                content: extractAssistantText(toReadableErrorMessage(msg?.content, '')),
                timestamp: new Date(msg.created_at), meta: msg.metadata
              })));
            } else {
              setMessages([]);
            }
          } catch { setMessages([]); }
          setWelcomeMessage(fallback.welcome_message || '');
        } else {
          setConversationId(null);
          setMessages([]);
          setWelcomeMessage('');
        }
      } else {
        setConversationId(null);
        setMessages([]);
        setWelcomeMessage('');
      }

      hasHydratedConversation.current = true;
    } catch (error) {
      console.warn('Failed to load conversation history:', error);
      if (messages.length === 0) {
        setMessages([]);
      }
    } finally {
      setIsConversationsLoading(false);
    }
  }, [conversationId, userId, companyId, messages.length, setMessages, setWelcomeMessage]);

  const loadMoreConversations = useCallback(async () => {
    if (!hasMoreConversations || isConversationsLoading) return;
    try {
      const conversationList = await aiApi.listConversations(CONVERSATIONS_PAGE_SIZE, conversationsOffset);
      const threads = (conversationList.conversations || []) as ConversationThread[];
      if (threads.length > 0) {
        setConversations(prev => [...prev, ...threads]);
        setConversationsOffset(prev => prev + threads.length);
      }
      setHasMoreConversations(threads.length >= CONVERSATIONS_PAGE_SIZE);
    } catch (error) {
      console.warn('Failed to load more conversations:', error);
    }
  }, [hasMoreConversations, isConversationsLoading, conversationsOffset]);

  const handleSelectConversation = useCallback(async (conversation: ConversationThread) => {
    try {
      setConversationId(conversation.id);
      setSuggestions([]);

      const history = await aiApi.getConversationMessages(conversation.id, 100, 0);
      const validMessages = (history.messages || []).map((msg: any) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        meta: msg.meta,
        suggestions: msg.suggestions,
        workflowStep: msg.workflow_step,
        attachments: msg.attachments
      }));

      setMessages(validMessages);
      setWelcomeMessage(conversation.welcome_message || '');

      try {
        localStorage.setItem('ai_active_conversation', conversation.id);
      } catch { /* ignore */ }
    } catch (error) {
      console.warn('Failed to load selected conversation:', error);
      setMessages([]);
    }
  }, [setMessages, setWelcomeMessage, setSuggestions]);

  const handleNewChat = useCallback(async () => {
    const hasUserMessages = messages.some(m => m.role === 'user');
    if (!hasUserMessages) return;

    try {
      const welcome = generateWelcomeMessage();
      const newConversation = await aiApi.createConversation('New Conversation', welcome);

      setMessages([]);
      setConversationId(newConversation.id);
      setSuggestions([]);
      setWelcomeMessage(welcome);

      const newSuggestions = generateSuggestions();
      setSuggestions(newSuggestions);

      try {
        localStorage.setItem('ai_active_conversation', newConversation.id);
      } catch { /* ignore */ }
    } catch (error) {
      console.warn('Failed to start new chat:', error);
    }
  }, [messages, generateWelcomeMessage, generateSuggestions, setMessages, setWelcomeMessage, setSuggestions]);

  const handleDeleteConversation = useCallback(async (convId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await aiApi.deleteConversation(convId);
      setConversations(prev => prev.filter(c => c.id !== convId));

      if (convId === conversationId) {
        const remaining = conversations.filter(c => c.id !== convId);
        if (remaining.length > 0 && remaining[0]) {
          handleSelectConversation(remaining[0]);
        } else {
          setConversationId(null);
          setMessages([]);
          setSuggestions([]);
          try { localStorage.removeItem('ai_active_conversation'); } catch { /* ignore */ }
        }
      }
    } catch (error) {
      console.warn('Failed to delete conversation:', error);
    }
  }, [conversationId, conversations, handleSelectConversation, setMessages, setSuggestions]);

  const handleExportConversation = useCallback((conv: ConversationThread, e: React.MouseEvent) => {
    e.stopPropagation();

    const isActive = conv.id === conversationId;
    const exportMessages = isActive ? messages : [];

    let text = `Conversation: ${conv.title || 'Untitled Chat'}\n`;
    text += `Date: ${new Date(conv.updated_at).toLocaleString()}\n`;
    text += `${'─'.repeat(60)}\n\n`;

    if (exportMessages.length === 0) {
      text += '(Load this conversation to export its messages)\n';
    } else {
      exportMessages.forEach(m => {
        const role = m.role === 'user' ? 'You' : 'Assistant';
        const time = new Date(m.timestamp).toLocaleTimeString();
        text += `[${time}] ${role}:\n${m.content}\n\n`;
      });
    }

    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${(conv.title || 'untitled').replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [conversationId, messages]);

  const ensureConversationId = useCallback(async (): Promise<string | null> => {
    if (conversationId) return conversationId;
    try {
      const welcome = generateWelcomeMessage();
      const createdConversation = await aiApi.createConversation('AI Assistant', welcome);
      setConversationId(createdConversation.id);
      setConversations(prev => [createdConversation as ConversationThread, ...prev]);
      return createdConversation.id;
    } catch (error) {
      console.warn('Failed to initialize conversation thread:', error);
      return null;
    }
  }, [conversationId]);

  const generateSmartTitle = useCallback((userMessage: string, _aiResponse: string): string => {
    const msg = userMessage.trim();

    const actionMatch = msg.match(/^(create|add|delete|remove|update|list|show|get|find|reset|suspend|deactivate|reactivate|assign|check|generate|export)\s+(.{1,40})/i);
    if (actionMatch) {
      const title = actionMatch[0].trim();
      return title.length > 60 ? title.slice(0, 57) + '…' : title;
    }

    const questionMatch = msg.match(/^(what|who|how|when|where|why|can|could|is|are|do|does|show me|tell me)\s+.+/i);
    if (questionMatch) {
      return msg.length > 60 ? msg.slice(0, 57).trim() + '…' : msg;
    }

    if (msg.length <= 60) return msg;
    const words = msg.slice(0, 60).split(' ');
    words.pop();
    return words.join(' ') + '…';
  }, []);

  return {
    conversationId,
    setConversationId,
    conversations,
    setConversations,
    isConversationsLoading,
    hasMoreConversations,
    loadConversationHistory,
    loadMoreConversations,
    handleSelectConversation,
    handleNewChat,
    handleDeleteConversation,
    handleExportConversation,
    ensureConversationId,
    generateSmartTitle,
    hasHydratedConversation,
  };
}
